import { Assert } from "../Assert";
import { PartialNullable, RadPromiseLike } from "../Extensions";
import { StringHelper } from "../Helpers/StringHelper";
import { JNative } from "../JNative";
import { PropertyChange } from "../PropertyChange";
import { IRadObject, Property, PropertyObject, RadObject, RadObjectPropertyValues } from "../RadObject";
import { NAMESPACE } from "./_Namespace";

export type BlobDownloaderPropertyValues =
    RadObjectPropertyValues
    & {
        headers: any;
    };

export type BlobDownloaderProperties = {
    [name in keyof BlobDownloaderPropertyValues]: Property<BlobDownloaderPropertyValues[name]>
};

export interface IBlobDownloader extends IRadObject, BlobDownloaderProperties
{
}

export class BlobDownloader extends RadObject implements IBlobDownloader
{
    public static override classNamespace = NAMESPACE + "BlobDownloader";
    public static override className = "BlobDownloader";

    // #region Properties

    public headers!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<BlobDownloaderPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public downloadAsync(url: string, $$link: JNative<HTMLAnchorElement>, overrideFileName?: string)
    {
        return (
            this.executeAsync(url)
                .then(response =>
                {
                    const
                        blob = response.blob,
                        xhr = response.xhr,
                        contentType = xhr.getResponseHeader("content-type");

                    Assert.isNotNullOrUndefined(contentType, "contentType");

                    let
                        fileName = overrideFileName || null;

                    if (!fileName)
                    {
                        const
                            contentDisposition = xhr.getResponseHeader("content-disposition");

                        if (!contentDisposition) // IE / EDGE seems not returning some response header
                            fileName = `unnamed.${contentType.substring(contentType.indexOf("/") + 1)}`;
                        else
                            fileName = contentDisposition.substring(contentDisposition.indexOf("=") + 1);

                        fileName = StringHelper.trim(fileName, "\"");
                    }

                    if ((window.navigator as any).msSaveOrOpenBlob) // Internet Explorer
                        (window.navigator as any).msSaveOrOpenBlob(new Blob([blob], { type: contentType ?? undefined }), fileName);
                    else
                    {
                        if ($$link)
                        {
                            $$link.attr("href", window.URL.createObjectURL(blob));
                            $$link.attr("download", overrideFileName ? overrideFileName : fileName);
                            $$link[0].click();
                        }
                    }

                    return Promise.resolve(blob);
                })
        );
    }
    public executeAsync(url: string): RadPromiseLike<{ blob: any, xhr: XMLHttpRequest }>
    {
        return new Promise((resolve, reject) =>
        {
            const
                headers = this.headers.get();

            let
                xhr = new XMLHttpRequest();

            xhr.open("GET", url, true);
            xhr.responseType = "blob";

            if (headers)
            {
                for (let header in headers)
                    xhr.setRequestHeader(header, headers[header]);
            }

            xhr.onload = evt =>
            {
                if (xhr.status >= 200 && xhr.status < 300)
                    resolve({ blob: xhr.response, xhr: xhr });
                else
                    reject({ message: `${xhr.status} error`, xhr: xhr });
            };

            xhr.onerror = error =>
            {
                reject({ message: error, xhr: xhr });
            };

            xhr.send();
        });
    }

    // #endregion

    // #region Protected Methods

    protected _headersChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: BlobDownloaderPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.headers = new PropertyObject(this, propertyValues.headers, this._headersChanged);
    }

    // #endregion
}

